import { POIShape } from './POIShape';
import { PlaneShape } from './PlaneShape';
import * as THREE from 'three';
import { AppSettingsData } from '../core/AppSettingsData';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { POI } from '../poi/POI';
import { Vector3 } from '../types';
import { Logger } from '../core/Logger';

/**
 * Layout modes for arranging multiple planes
 */
export enum MultiPlaneLayout {
    GRID = 'grid',
    CIRCLE = 'circle', 
    LINE = 'line',
    STACK = 'stack'  // Planes hintereinander entlang Z-Achse
}

/**
 * MultiPlaneShape - Creates multiple PlaneShapes from semicolon-separated URIs
 * 
 * Features:
 * - Parses multiple URIs (semicolon-separated)
 * - Creates a PlaneShape for each URI
 * - Supports different layout modes (grid, circle, line)
 * - Automatic spacing and positioning
 * - Event-driven updates when URIs change
 */
export class MultiPlaneShape extends POIShape {
    private _planes: PlaneShape[] = [];
    private _layout: MultiPlaneLayout = MultiPlaneLayout.STACK;  // Standard: Stapel-Layout
    private _spacing: number = 1.5;
    private _currentUris: string[] = [];

    constructor(
        poi: POI,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData
    ) {
        super(poi, sceneSettings, appSettings);
        
        // Initial creation of planes
        this.recreatePlanes();
        
        // Setup event listeners for property changes (after materials are initialized)
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        // Listen for URI changes
        this.poi.poidata.uri?.on(() => {
            this.recreatePlanes();
        });

        // Listen for layout changes (if we add layout properties later)
        this.poi.poidata.cornerRadius?.on(() => {
            this.updateAllPlanes();
        });

        // Listen for material aspect ratio changes (with safety check)
        if (this.primaryMaterial && this.primaryMaterial.aspectRatio) {
            this.primaryMaterial.aspectRatio.on(() => {
                this.updateAllPlanes();
            });
        }

        // Listen for scene settings changes
        this.sceneSettings?.geometryResolution.on(() => {
            this.updateAllPlanes();
        });
    }

    /**
     * Recreate all planes when URIs change
     */
    private recreatePlanes(): void {
        const newUris = this.getUrisFromPOI();
        
        // Check if URIs actually changed
        if (this.arraysEqual(this._currentUris, newUris)) {
            return;
        }

        // Clear existing planes
        this.clearExistingPlanes();

        // Store current URIs
        this._currentUris = [...newUris];

        // Create new planes
        this.createPlanesForUris(newUris);

        // Arrange planes in layout
        this.arrangePlaynes();

        // Rebuild mesh to include all planes
        this.buildMesh();
    }

    /**
     * Get URIs array from POI data
     */
    private getUrisFromPOI(): string[] {
        const uriValue = this.poi.poidata.uri?.value;
        
        if (!uriValue) {
            return [];
        }

        // Handle array format (should already be array from MarkdownParser)
        if (Array.isArray(uriValue)) {
            return uriValue.filter((s: string) => s && s.trim() !== '');
        }

        return [];
    }

    /**
     * Clear all existing planes
     */
    private clearExistingPlanes(): void {
        for (const plane of this._planes) {
            // Remove plane from scene if it's added
            if ((plane as any)._mesh && (plane as any)._mesh.parent) {
                (plane as any)._mesh.parent.remove((plane as any)._mesh);
            }
            // Clean up plane resources - call public dispose if available
        }
        this._planes = [];
    }

    /**
     * Create PlaneShapes for each URI
     */
    private createPlanesForUris(uris: string[]): void {
        for (let i = 0; i < uris.length; i++) {
            const uri = uris[i];

            // Create a modified POI for this plane
            const planePOI = this.createPlaneSpecificPOI(uri, i);
            
            // Create PlaneShape
            const plane = new PlaneShape(planePOI, this.sceneSettings, this.appSettings);
            
            this._planes.push(plane);
        }
    }

    /**
     * Create a POI with single URI or color for individual planes
     */
    private createPlaneSpecificPOI(uriOrColor: string, index: number): POI {
        // Detect the type of input
        const planeConfig = this.detectPlaneType(uriOrColor);
        
        // Create new POIData instance (safer than Object.create)
        const planePOIData = new (this.poi.poidata.constructor as any)(
            this.sceneSettings,
            this.appSettings
        );
        
        // Copy relevant properties from parent POI
        planePOIData.cornerRadius.value = this.poi.poidata.cornerRadius?.value ?? 0;
        const planeScale = this.getScaleForPlane(index);
        planePOIData.scale.value = planeScale;
        Logger.Instance.Info(`MultiPlane ${this.poi.name}: plane[${index}] assigned scale=${planeScale}`, 'MultiPlane');
        planePOIData.position.value = this.poi.poidata.position?.value;
        planePOIData.rotation.value = this.poi.poidata.rotation?.value;
        planePOIData.aspectRatio.value = this.poi.poidata.aspectRatio?.value ?? 0; // Inherit aspect ratio

        // Set individual plane opacity
        const planeOpacityValue = this.getOpacityForPlane(index);
        planePOIData.opacity.value = planeOpacityValue;
        
        // Set shape to plane
        planePOIData.shape.value = 'plane';
        
        // 🚫 DISABLE LABELS FOR SUB-PLANES: Only the main POI should have labels
        planePOIData.showLabel.value = false;
        
        //console.log(`🛠️ [MultiPlane] Creating sub-POI: "${this.poi.name}_plane_${index}" with showLabel=false`);
        
        // Configure URI or color based on detection
        if (planeConfig.uri) {
            // Texture plane
            planePOIData.uri.value = [planeConfig.uri];
        } else if (planeConfig.color) {
            // Color plane
            planePOIData.uri.value = []; // No texture
            planePOIData.color.value = planeConfig.color;
        } else {
            // Fallback
            planePOIData.uri.value = [];
            planePOIData.color.value = this.poi.poidata.color?.value;
        }
        
        const planePOI = new POI(
            `${this.poi.name}_plane_${index}`,
            planePOIData,
            this.sceneSettings,
            this.appSettings
        );
        
        //console.log(`✅ [MultiPlane] Sub-POI created: "${planePOI.name}" with showLabel=${planePOI.poidata.showLabel.value}`);
        
        return planePOI;
    }

    /**
     * Detect whether input is a URI (texture) or color (solid)
     */
    private detectPlaneType(input: string): { uri?: string, color?: any } {
        const trimmed = input.trim();
        
        // Empty input = use default color
        if (!trimmed) {
            return { color: this.poi.poidata.color?.value };
        }
        
        // Hex color: #FF0000, #00AA55
        if (trimmed.match(/^#[0-9A-Fa-f]{6}$/)) {
            return { color: this.parseHexColor(trimmed) };
        }
        
        // CSS named colors: red, blue, green, etc.
        if (this.isNamedColor(trimmed)) {
            return { color: this.parseNamedColor(trimmed) };
        }
        
        // Everything else is treated as URI (image/video)
        return { uri: trimmed };
    }
    
    /**
     * Parse hex color to Color object
     */
    private parseHexColor(hex: string): any {
        const r = parseInt(hex.substring(1, 3), 16) / 255;
        const g = parseInt(hex.substring(3, 5), 16) / 255;
        const b = parseInt(hex.substring(5, 7), 16) / 255;
        
        // Return Color-like object (compatible with Color class)
        return { r, g, b, a: 1.0 };
    }
    
    /**
     * Check if string is a valid CSS named color
     */
    private isNamedColor(name: string): boolean {
        const namedColors = [
            'red', 'green', 'blue', 'yellow', 'orange', 'purple', 'pink', 'brown',
            'black', 'white', 'gray', 'grey', 'cyan', 'magenta', 'lime', 'maroon',
            'navy', 'olive', 'silver', 'teal', 'aqua', 'fuchsia'
        ];
        return namedColors.includes(name.toLowerCase());
    }
    
    /**
     * Parse named color to Color object
     */
    private parseNamedColor(name: string): any {
        const colorMap: { [key: string]: { r: number, g: number, b: number } } = {
            'red': { r: 1, g: 0, b: 0 },
            'green': { r: 0, g: 0.5, b: 0 },
            'blue': { r: 0, g: 0, b: 1 },
            'yellow': { r: 1, g: 1, b: 0 },
            'orange': { r: 1, g: 0.65, b: 0 },
            'purple': { r: 0.5, g: 0, b: 0.5 },
            'pink': { r: 1, g: 0.75, b: 0.8 },
            'brown': { r: 0.65, g: 0.16, b: 0.16 },
            'black': { r: 0, g: 0, b: 0 },
            'white': { r: 1, g: 1, b: 1 },
            'gray': { r: 0.5, g: 0.5, b: 0.5 },
            'grey': { r: 0.5, g: 0.5, b: 0.5 },
            'cyan': { r: 0, g: 1, b: 1 },
            'magenta': { r: 1, g: 0, b: 1 },
            'lime': { r: 0, g: 1, b: 0 },
            'maroon': { r: 0.5, g: 0, b: 0 },
            'navy': { r: 0, g: 0, b: 0.5 },
            'olive': { r: 0.5, g: 0.5, b: 0 },
            'silver': { r: 0.75, g: 0.75, b: 0.75 },
            'teal': { r: 0, g: 0.5, b: 0.5 },
            'aqua': { r: 0, g: 1, b: 1 },
            'fuchsia': { r: 1, g: 0, b: 1 }
        };
        
        const color = colorMap[name.toLowerCase()];
        return color ? { ...color, a: 1.0 } : { r: 0.5, g: 0.5, b: 0.5, a: 1.0 };
    }

    /**
     * Arrange planes according to layout mode
     */
    private arrangePlaynes(): void {
        const count = this._planes.length;
        if (count === 0) return;



        switch (this._layout) {
            case MultiPlaneLayout.GRID:
                this.arrangeInGrid();
                break;
            case MultiPlaneLayout.CIRCLE:
                this.arrangeInCircle();
                break;
            case MultiPlaneLayout.LINE:
                this.arrangeInLine();
                break;
            case MultiPlaneLayout.STACK:
                this.arrangeInStack();
                break;
        }
    }

    /**
     * Arrange planes in a grid layout
     */
    private arrangeInGrid(): void {
        const count = this._planes.length;
        const cols = Math.ceil(Math.sqrt(count));
        const rows = Math.ceil(count / cols);

        for (let i = 0; i < count; i++) {
            const plane = this._planes[i];
            const col = i % cols;
            const row = Math.floor(i / cols);

            // Center the grid
            const offsetX = (cols - 1) * this._spacing * -0.5;
            const offsetY = (rows - 1) * this._spacing * -0.5;

            const x = offsetX + col * this._spacing;
            const y = offsetY + row * this._spacing;

            plane.transform.position.value = new Vector3(x, y, 0);
        }
    }

    /**
     * Arrange planes in a circle layout
     */
    private arrangeInCircle(): void {
        const count = this._planes.length;
        const radius = this._spacing * count / (2 * Math.PI);

        for (let i = 0; i < count; i++) {
            const plane = this._planes[i];
            const angle = (i / count) * Math.PI * 2;
            
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;

            plane.transform.position.value = new Vector3(x, y, 0);
        }
    }

    /**
     * Arrange planes in a line layout
     */
    private arrangeInLine(): void {
        const count = this._planes.length;

        for (let i = 0; i < count; i++) {
            const plane = this._planes[i];
            
            // Center the line
            const offset = (count - 1) * this._spacing * -0.5;
            const x = offset + i * this._spacing;

            plane.transform.position.value = new Vector3(x, 0, 0);
        }
    }

    /**
     * Arrange planes in a stack layout (hintereinander entlang Z-Achse)
     * Verwendet das depth Setting für den Abstand
     */
    private arrangeInStack(): void {
        const count = this._planes.length;

        // Hole depth-Wert aus POI settings
        const depthSpacing = this.poi.poidata.depth?.value ?? 0.1;
        Logger.Instance.Info(`MultiPlane ${this.poi.name}: arrangeInStack count=${count} depthSpacing=${depthSpacing}`, 'MultiPlane');

        for (let i = 0; i < count; i++) {
            const plane = this._planes[i];

            // Arrange entlang Z-Achse, zentriert
            const offset = (count - 1) * depthSpacing * -0.5;
            const z = offset + i * depthSpacing;

            plane.transform.position.value = new Vector3(0, 0, z);

            // Apply per-plane scale to the PlaneShape's SceneNode transform directly.
            // Setting planePOIData.scale only propagates to planePOI.transform — not to the
            // PlaneShape's transform (which is the one that gets synced to the Three.js mesh).
            const s = this.getScaleForPlane(i);
            plane.transform.scale.value = new Vector3(s, s, s);
            Logger.Instance.Info(`MultiPlane ${this.poi.name}: plane[${i}] -> z=${z} scale=${s}`, 'MultiPlane');
        }
    }

    /**
     * Update all existing planes (geometry/material changes)
     */
    private updateAllPlanes(): void {
        for (const plane of this._planes) {
            // Trigger geometry recreation by calling buildMesh
            (plane as any).buildMesh?.();
        }
    }

    /**
     * Build the combined mesh from all planes
     */
    protected override buildMesh(): void {
        // Safety check: buildMesh can be called before planes are created
        if (!this._planes || this._planes.length === 0) {
            // Create empty group as placeholder
            (this as any)._mesh = new THREE.Group();
            return;
        }

        // Clear existing mesh
        if ((this as any)._mesh) {
            (this as any)._mesh.clear();
        } else {
            (this as any)._mesh = new THREE.Group();
        }

        // Add all plane meshes to the group
        for (const plane of this._planes) {
            if ((plane as any)._mesh) {
                (this as any)._mesh.add((plane as any)._mesh);
            }
        }
    }

    /**
     * Required by POIShape - we don't create geometry directly
     */
    protected override createGeometry(): THREE.BufferGeometry {
        // MultiPlaneShape doesn't create its own geometry, it uses child planes
        return new THREE.BufferGeometry();
    }

    /**
     * Override onUpdate to update all child planes.
     * We call plane.update() (not just onUpdate) so that syncTransformToThreeJS()
     * runs on each plane — otherwise the local position set by arrangeInStack
     * never propagates to plane._mesh.position, and planes collapse to z=0 (z-fighting).
     * The planes are attached as Three.js children of this group but are NOT
     * registered as SceneNode children, so the scene's update loop doesn't reach them.
     */
    protected override onUpdate(deltaTime: number): void {
        // Update all child planes (full update including transform sync)
        for (const plane of this._planes) {
            (plane as any).update?.(deltaTime);
        }

        // Call parent update
        super.onUpdate(deltaTime);
    }

    /**
     * Utility to compare arrays
     */
    private arraysEqual(arr1: string[], arr2: string[]): boolean {
        if (arr1.length !== arr2.length) return false;
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) return false;
        }
        return true;
    }

    /**
     * Set layout mode
     */
    public setLayout(layout: MultiPlaneLayout): void {
        if (this._layout !== layout) {
            this._layout = layout;
            this.arrangePlaynes();
            this.buildMesh();
        }
    }

    /**
     * Set spacing between planes
     */
    public setSpacing(spacing: number): void {
        if (this._spacing !== spacing) {
            this._spacing = spacing;
            this.arrangePlaynes();
            this.buildMesh();
        }
    }

    /**
     * Get layout mode
     */
    public getLayout(): MultiPlaneLayout {
        return this._layout;
    }

    /**
     * Get spacing
     */
    public getSpacing(): number {
        return this._spacing;
    }

    /**
     * Get number of planes
     */
    public getPlaneCount(): number {
        return this._planes.length;
    }

    /**
     * Get all plane shapes
     */
    public getPlanes(): PlaneShape[] {
        return [...this._planes];
    }

    /**
     * Get opacity value for a specific plane index
     */
    private getOpacityForPlane(index: number): number {
        const opacityArray = this.poi.poidata.planeOpacity?.value ?? [];
        if (!Array.isArray(opacityArray) || opacityArray.length === 0) {
            // No planeOpacity specified → use global opacity
            return this.poi.poidata.opacity?.value ?? 1.0;
        }
        
        // Validate values (0-1 range)
        const validOpacityValues = opacityArray.filter(n => 
            typeof n === 'number' && !isNaN(n) && n >= 0 && n <= 1
        );
        
        if (validOpacityValues.length === 0) {
            // Invalid array → fallback to global opacity
            return this.poi.poidata.opacity?.value ?? 1.0;
        }
        
        // If index is beyond array, use the last value
        const opacityIndex = Math.min(index, validOpacityValues.length - 1);
        return validOpacityValues[opacityIndex];
    }

    /**
     * Get scale value for a specific plane index.
     * Falls back to the parent POI's uniform scale when planeScale array is empty.
     */
    private getScaleForPlane(index: number): number {
        const scaleArray = this.poi.poidata.planeScale?.value ?? [];
        const uniformScale = this.poi.poidata.scale?.value ?? 1;
        if (!Array.isArray(scaleArray) || scaleArray.length === 0) {
            return uniformScale;
        }
        const validScales = scaleArray.filter(n => typeof n === 'number' && !isNaN(n) && n > 0);
        if (validScales.length === 0) return uniformScale;
        // If index beyond array, repeat the last value
        const scaleIndex = Math.min(index, validScales.length - 1);
        return validScales[scaleIndex];
    }

    /**
     * Clean up resources
     */
    public override dispose(): void {
        this.clearExistingPlanes();
        super.dispose?.();
    }
}